/**
 * Square brand mark for a business.
 *
 * Renders `logo_url` (the scraped/auto-fetched logo) when present, otherwise
 * shows initials on a navy/gold tile that matches the site palette. Used
 * everywhere a business is listed (directory, category pages, featured rail,
 * marketplace business chip, etc.) so the visual identity is consistent.
 */
import { useState } from "react";
import { resolveAssetUrl as absoluteLogoUrl } from "@/lib/assetUrl";

function initialsOf(name) {
  if (!name) return "TBC";
  const parts = String(name)
    .replace(/[^a-zA-Z0-9\s]/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return "TBC";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

/**
 * <BusinessLogo
 *   name="..."
 *   logoUrl="..."
 *   size="md"        // sm | md | lg | xl
 *   shape="rounded"  // rounded | square | circle
 *   className=""
 * />
 */
export default function BusinessLogo({
  name,
  logoUrl,
  size = "md",
  shape = "rounded",
  className = "",
}) {
  const [errored, setErrored] = useState(false);

  const resolved = !errored ? absoluteLogoUrl(logoUrl) : null;
  const initials = initialsOf(name);

  const sizeClasses = {
    sm: "w-10 h-10 text-sm",
    md: "w-16 h-16 text-lg",
    lg: "w-24 h-24 text-2xl",
    xl: "w-36 h-36 text-4xl",
    "2xl": "w-56 h-56 text-6xl",
    "3xl": "w-72 h-72 text-7xl",
  }[size] || "w-16 h-16 text-lg";

  const shapeClasses = {
    rounded: "rounded-xl",
    square: "rounded-none",
    circle: "rounded-full",
  }[shape] || "rounded-xl";

  const base = `relative inline-flex items-center justify-center overflow-hidden ${sizeClasses} ${shapeClasses} ${className}`;

  if (resolved) {
    // Square container with black bg so dark-bg logos (gold-on-black, white-on-navy, etc)
    // blend seamlessly with the letterbox bars produced by `object-contain`. Logos
    // with their own white background still look right because we keep `object-contain`
    // and never crop.
    return (
      <div
        className={`${base} bg-black`}
        data-testid="business-logo"
      >
        <img
          src={resolved}
          alt={`${name || "Business"} logo`}
          className="w-full h-full object-contain"
          onError={() => setErrored(true)}
          loading="lazy"
        />
      </div>
    );
  }

  // Initials placeholder — navy tile with gold text, on-brand
  return (
    <div
      className={`${base} bg-tbc-navy text-tbc-gold font-heading font-bold tracking-wide`}
      data-testid="business-logo-initials"
      aria-label={`${name || "Business"} logo placeholder`}
    >
      <span>{initials}</span>
    </div>
  );
}
